import { fetchPagePublisherJson } from './pagePublisherApi'

type Section = {
  id: string
  label: string
}

const CACHE_TTL_MS = 60 * 1000
// After a failed fetch, wait this long before trying again so an orders-engine
// outage doesn't add a request (and its timeout) to every page view.
const RETRY_AFTER_ERROR_MS = 10 * 1000

let cache: { ids: Set<string>; labels: Map<string, string>; expiresAt: number } | undefined
let inFlight: Promise<Set<string>> | undefined

async function loadSectionIds(): Promise<Set<string>> {
  try {
    const sections = await fetchPagePublisherJson('/sections')
    const list = Array.isArray(sections) ? (sections as Section[]) : []
    const ids = new Set(list.map((section) => section.id))
    const labels = new Map(list.map((section) => [section.id, section.label]))
    cache = { ids, labels, expiresAt: Date.now() + CACHE_TTL_MS }
    return ids
  } catch (error) {
    console.error('Failed to load Page Publisher sections', error)
    // Keep serving the last known list; with none, no path is a section.
    const ids = cache?.ids ?? new Set<string>()
    const labels = cache?.labels ?? new Map<string, string>()
    cache = { ids, labels, expiresAt: Date.now() + RETRY_AFTER_ERROR_MS }
    return ids
  }
}

/**
 * The sections registry (e.g. `/guides`, `/tools`) is managed from the Page
 * Publisher dashboard and served by templi-orders-engine. It's checked by
 * `middleware.ts` on every page request, so it's cached in memory for a
 * minute: a newly added section goes live within about a minute, without a
 * deploy. A section's `id` is its URL prefix (`templi.com/{id}/...`).
 */
export async function getSectionIds(): Promise<Set<string>> {
  if (cache && cache.expiresAt > Date.now()) return cache.ids
  if (!inFlight) {
    inFlight = loadSectionIds().finally(() => {
      inFlight = undefined
    })
  }
  return inFlight
}

export async function isValidSection(id: string): Promise<boolean> {
  return (await getSectionIds()).has(id)
}

/** A section's display name (e.g. "Guides"), shown as the eyebrow above a page's H1. */
export async function getSectionLabel(id: string): Promise<string | undefined> {
  await getSectionIds()
  return cache?.labels.get(id)
}

/** Public (canonical) path of a published page; used by the page itself and the sitemap. */
export function publishedPagePath(section: string, slug: string): string {
  return `/${section}/${slug}`
}
